import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Search, Save, Archive, ArchiveRestore, Shield } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import SidePanel from "@/components/calendar/SidePanel";
import { useAuth } from "@/context/AuthContext";
import { canAccessOwnerFrontDeskSettings } from "@/lib/settingsAccess";

// =====================================================================
// Settings → Insurance Payers (HMOs)
//
// Manages the clinic's accepted-HMO list (Maxicare, Medicard, etc.).
// Used at checkout when the receptionist captures HMO coverage on a
// patient's visit (LOA #, covered amount, payer).
// =====================================================================

export default function SettingsInsurance() {
  const { clinicRole, isPlatformAdmin } = useAuth();
  const canManageInsurance = canAccessOwnerFrontDeskSettings(clinicRole, isPlatformAdmin);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [rows, setRows]         = useState([]);
  const [search, setSearch]     = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [panel, setPanel]       = useState(null);

  const load = useCallback(async () => {
    if (!canManageInsurance) {
      setLoading(false);
      return;
    }
    setLoading(true); setError(null);
    const { data, error: e } = await supabase.rpc("list_insurance_payers", { p_include_archived: showArchived });
    setLoading(false);
    if (e) { setError(e.message ?? "Could not load."); return; }
    setRows(data ?? []);
  }, [canManageInsurance, showArchived]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(q)
      || (r.short_name || "").toLowerCase().includes(q));
  }, [rows, search]);

  if (!canManageInsurance || (error && /permission denied/i.test(error))) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center" data-testid="ins-denied">
        <Shield className="w-8 h-8 mx-auto text-slate-300" />
        <p className="text-sm text-slate-600 mt-3">Only clinic owners and front desk can manage Insurance Payers.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="settings-insurance">
      <div className="bg-white rounded-2xl shadow-sm px-5 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Insurance payers</h1>
            <p className="text-xs text-slate-500">The HMOs and insurance providers your clinic accepts. Used at checkout to capture coverage.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
                data-testid="ins-show-archived"
                className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600" />
              Show archived
            </label>
            <button type="button" onClick={() => setPanel({ mode: "new" })}
              data-testid="ins-add"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded">
              <Plus className="w-3.5 h-3.5" />Add payer
            </button>
          </div>
        </div>
        <div className="mt-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input type="search" placeholder="Search payers…" value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="ins-search"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
        </div>
      </div>

      {error && !/permission denied/i.test(error) ? (
        <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
      ) : null}

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
          <Shield className="w-8 h-8 mx-auto text-slate-300" />
          <p className="text-sm text-slate-600 mt-3">No insurance payers yet.</p>
          <button type="button" onClick={() => setPanel({ mode: "new" })}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded">
            <Plus className="w-3.5 h-3.5" />Add your first payer
          </button>
        </div>
      ) : (
        <ul className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100" data-testid="ins-list">
          {filtered.map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => setPanel({ mode: "edit", row: r })}
                data-testid={`ins-row-${r.id}`}
                className={`w-full text-left px-5 py-3 flex items-start gap-4 hover:bg-slate-50 ${r.archived ? "opacity-60" : ""}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-800 truncate">{r.name}</p>
                    {r.short_name ? <span className="text-xs text-slate-500">({r.short_name})</span> : null}
                    {r.archived ? <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Archived</span> : null}
                  </div>
                  {r.notes ? <p className="text-xs text-slate-500 mt-0.5 truncate">{r.notes}</p> : null}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <SidePanel
        open={!!panel}
        onClose={() => setPanel(null)}
        title={panel?.mode === "new" ? "Add insurance payer" : "Edit payer"}
      >
        {panel ? (
          <EditPayer
            mode={panel.mode}
            row={panel.row}
            onDone={() => { setPanel(null); load(); }}
            onCancel={() => setPanel(null)}
          />
        ) : null}
      </SidePanel>
    </div>
  );
}

function EditPayer({ mode, row, onDone, onCancel }) {
  const [name, setName]       = useState(row?.name ?? "");
  const [shortName, setShortName] = useState(row?.short_name ?? "");
  const [notes, setNotes]     = useState(row?.notes ?? "");
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);

  async function handleSave() {
    if (!name.trim()) { setError("Name is required."); return; }
    setError(null); setBusy(true);
    if (mode === "new") {
      const { error: e } = await supabase.rpc("create_insurance_payer", {
        p_input: { name: name.trim(), short_name: shortName.trim() || null, notes: notes.trim() || null },
      });
      setBusy(false);
      if (e) { setError(e.message ?? "Save failed."); return; }
      toast.success("Payer added.");
    } else {
      const { error: e } = await supabase.rpc("update_insurance_payer", {
        p_input: { id: row.id, name: name.trim(), short_name: shortName.trim() || null, notes: notes.trim() || null },
      });
      setBusy(false);
      if (e) { setError(e.message ?? "Save failed."); return; }
      toast.success("Payer updated.");
    }
    onDone?.();
  }

  async function handleArchiveToggle() {
    setError(null); setBusy(true);
    const { error: e } = await supabase.rpc("set_insurance_payer_archived", {
      p_id: row.id, p_archived: !row.archived,
    });
    setBusy(false);
    if (e) { setError(e.message ?? "Failed."); return; }
    toast.success(row.archived ? "Restored." : "Archived.");
    onDone?.();
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="ins-name">Name</label>
          <input id="ins-name" value={name} onChange={(e) => setName(e.target.value)}
            data-testid="ins-name" placeholder="e.g. Maxicare"
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="ins-short">Short name (optional)</label>
          <input id="ins-short" value={shortName} onChange={(e) => setShortName(e.target.value)}
            data-testid="ins-short" placeholder="e.g. MX"
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
          <p className="text-[11px] text-slate-400 mt-1">Used as a tighter label in tables and reports.</p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500" htmlFor="ins-notes">Notes (optional)</label>
          <textarea id="ins-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
            data-testid="ins-notes" rows={3} placeholder="Submission instructions, contact, accreditation #, etc."
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none" />
        </div>
        {error ? <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div> : null}
      </div>

      <div className="border-t border-slate-200 px-5 py-3 flex items-center gap-2 bg-white">
        {mode === "edit" ? (
          <button type="button" onClick={handleArchiveToggle} disabled={busy}
            data-testid="ins-archive"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-50">
            {row.archived ? <><ArchiveRestore className="w-3.5 h-3.5" />Restore</> : <><Archive className="w-3.5 h-3.5" />Archive</>}
          </button>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={onCancel} disabled={busy}
            className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 rounded">Cancel</button>
          <button type="button" onClick={handleSave} disabled={busy}
            data-testid="ins-save"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
